// TimerMgr + Timer — the tick-driven timer scan. game/timer.cpp + game/ht.h:453-499.
//
// In the SIM, Simulation IS-A TimerMgr whose GetTickCount() returns m_tCurrent (sim ticks,
// deterministic) — and Simulation::AddTimer asserts "you are the first user": no sim timers
// exist in the shipping game, so gsim's ScanDispatch runs over an empty list. Ported fully
// anyway: it is small, in scope, and the structure is what gsmm.Init binds to.

export abstract class Timer {
  m_ptimm: TimerMgr | null = null;
  m_ptmrNext: Timer | null = null;
  m_ptmrPrev: Timer | null = null;
  m_ctRate = 0;
  m_tTrigger = 0;

  abstract OnTimer(tCurrent: number): void;
}

export class TimerMgr {
  private m_ptmrFirst: Timer | null = null;
  private m_ptmrNotifying: Timer | null = null;
  private m_ptmrTriggerNext: Timer | null = null;
  private m_fForceScan = false;
  private m_fEnabled = true;

  /** Overridden by Simulation to return sim time (m_tCurrent). game/timer.cpp:53-63. */
  GetTickCount(): number {
    throw new Error('TimerMgr.GetTickCount must be overridden by the sim clock owner');
  }

  Enable(fEnable: boolean): void {
    this.m_fEnabled = fEnable;
  } // ht.h:467-469

  // game/timer.cpp:65-149
  ScanDispatch(tCurrent: number): number {
    if (!this.m_fEnabled) return -1;
    if (this.m_ptmrNotifying !== null) return -1;

    if (!this.m_fForceScan) {
      if (this.m_ptmrTriggerNext !== null) {
        const ctT = this.m_ptmrTriggerNext.m_tTrigger - tCurrent;
        if (ctT > 0) return ctT;
      }
    }
    this.m_fForceScan = false;

    let ptmrT = this.m_ptmrFirst;
    while (ptmrT !== null) {
      const ptmrNext = ptmrT.m_ptmrNext;
      if (ptmrT.m_tTrigger <= tCurrent) {
        this.m_ptmrNotifying = ptmrT;
        ptmrT.OnTimer(tCurrent);
        this.m_ptmrNotifying = null;

        const fRemoved = ptmrT.m_tTrigger === -1;
        if (!fRemoved) {
          ptmrT.m_tTrigger += ptmrT.m_ctRate;
          // catch a lagging timer up to "now" (the sim clock under Simulation)
          tCurrent = this.GetTickCount();
          if (ptmrT.m_tTrigger < tCurrent) ptmrT.m_tTrigger = tCurrent;
        }
      }
      ptmrT = ptmrNext;
    }

    this.m_ptmrTriggerNext = this.m_ptmrFirst;
    for (let p = this.m_ptmrFirst; p !== null; p = p.m_ptmrNext) {
      if (p.m_tTrigger < this.m_ptmrTriggerNext!.m_tTrigger) this.m_ptmrTriggerNext = p;
    }

    if (this.m_ptmrTriggerNext === null) return -1;
    const ct = tCurrent - this.m_ptmrTriggerNext.m_tTrigger;
    if (ct < 0) return 0;
    return ct;
  }

  // game/timer.cpp:151-170 — prepend; duplicate add is a no-op
  AddTimer(ptmr: Timer, ct: number): void {
    for (let p = this.m_ptmrFirst; p !== null; p = p.m_ptmrNext) {
      if (p === ptmr) return;
    }
    if (this.m_ptmrFirst !== null) this.m_ptmrFirst.m_ptmrPrev = ptmr;
    ptmr.m_ptmrPrev = null;
    ptmr.m_ptmrNext = this.m_ptmrFirst;
    ptmr.m_ptimm = this;
    this.m_ptmrFirst = ptmr;
    this.SetTimerRate(ptmr, ct);
  }

  // game/timer.cpp:172-201
  RemoveTimer(ptmr: Timer): void {
    let fFound = false;
    for (let p = this.m_ptmrFirst; p !== null; p = p.m_ptmrNext) {
      if (p === ptmr) {
        fFound = true;
        break;
      }
    }
    if (!fFound) return;

    ptmr.m_tTrigger = -1;
    ptmr.m_ptimm = null;

    if (ptmr.m_ptmrPrev !== null) ptmr.m_ptmrPrev.m_ptmrNext = ptmr.m_ptmrNext;
    else this.m_ptmrFirst = ptmr.m_ptmrNext;
    if (ptmr.m_ptmrNext !== null) ptmr.m_ptmrNext.m_ptmrPrev = ptmr.m_ptmrPrev;

    if (this.m_ptmrNotifying !== ptmr && this.m_ptmrTriggerNext === ptmr) {
      this.m_ptmrTriggerNext = null;
      this.m_fForceScan = true;
    }
  }

  // game/timer.cpp:203-211
  SetTimerRate(ptmr: Timer, ct: number): void {
    ptmr.m_ctRate = ct;
    ptmr.m_tTrigger = this.GetTickCount() + ct;
    this.m_fForceScan = true;
  }

  // game/timer.cpp:213-216
  BoostTimer(ptmr: Timer, ct: number): void {
    ptmr.m_tTrigger += ct;
    this.m_fForceScan = true;
  }

  // game/timer.cpp:221-225
  TriggerTimer(ptmr: Timer): void {
    ptmr.m_tTrigger = this.GetTickCount();
    this.m_fForceScan = true;
  }

  // game/timer.cpp:228-238
  IsAdded(ptmr: Timer): boolean {
    for (let p = this.m_ptmrFirst; p !== null; p = p.m_ptmrNext) {
      if (p === ptmr) return true;
    }
    return false;
  }
}
